import React, { Component } from "react";
import { Dropdown, Icon, Input, Menu, Modal, Table, Typography, Select } from "antd";
import { Typography as MuiTypography } from "@material-ui/core";
import styles from "./style.module.scss";
import { MUTATE, QUERY } from "../../utilities/network";
import { observer } from "mobx-react";
import { toast } from "react-toastify";
import { computed, observable, runInAction, toJS } from "mobx";
import dayjs from "dayjs";
import CONST from "../../utilities/constant";
import Membership from "../../store/membership";
import { RouteComponentProps } from "react-router";
import { Button, Grid } from "@material-ui/core";
import { getAllMembers, postBulkEdit, postMoveGroups } from "../../requests/dashboard";

type StatusEnum = "Active" | "Not activated" | "Deactivated";
const { Option } = Select;

@observer
export default class MemberList extends Component<RouteComponentProps<{}>> {
  @observable pageSize = 100;
  @observable groups: Array<GroupType> = [];
  @observable totalMembers: number = 0;
  @observable loading = false;
  @observable members: Array<GroupMemberType> = [];
  @observable keyword = "";
  @observable bulkSelected: Array<GroupMemberType> = [];
  @observable value: any = "";
  @observable allMemberData: any = [];

  filteredGroupIds: Array<number> = [];
  filteredStatus: Array<StatusEnum> = [];

  @computed
  get groupFilters(): Array<{ text: string; value: number }> {
    return this.groups.map((group) => ({
      text: group.name,
      value: group.group_id,
    }));
  }

  get userStatusFilters() {
    return [
      { text: "Active", value: "Active" },
      { text: "Not activated", value: "Not activated" },
      { text: "Deactivated", value: "Deactivated" },
    ];
  }

  get active30DaysFilters() {
    return [
      { text: "Active", value: "Active" },
      { text: "Passive", value: "Passive" },
    ];
  }

  @computed
  get columns(): Array<any> {
    return [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 100,
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 200,
      },
      {
        title: "Group",
        dataIndex: "group_name",
        key: "group",
        width: 150,
        filters: this.groupFilters,
        onFilter: (groupId: number, member: GroupMemberType) => member.group_id === groupId,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        filters: this.userStatusFilters,
        onFilter: (status: StatusEnum, member: GroupMemberType) => {
          if (status === "Deactivated") {
            return member.membership.valid_days === 0;
          } else if (status === "Not activated") {
            return member.status === "Inactivated";
          }
          return member.membership.valid_days !== 0 && member.status === "Activated";
        },
        render: (status: "Inactivated" | "Activated", member: GroupMemberType) => {
          if (member.membership.valid_days === 0) {
            return <Typography.Text type="warning">Deactivated</Typography.Text>;
          }
          if (status === "Inactivated") {
            return <Typography.Text type="danger">Not activated</Typography.Text>;
          }
          return <Typography.Text style={{ color: "#52c41a" }}>Logged in</Typography.Text>;
        },
      },
      {
        title: "Active last 30 days",
        dataIndex: "active_in_30_days",
        key: "active_in_30_days",
        width: 100,
        align: "center",
        filters: this.active30DaysFilters,
        onFilter: (status: "Active" | "Passive", member: GroupMemberType) =>
          member.active_in_30_days === (status === "Active"),
        render: (active_in_30_days: boolean) => {
          const backgroundColor = active_in_30_days ? "#52c41a" : "#f5222d";
          return (
            <div className={styles.active_30_days}>
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor,
                }}
              />
            </div>
          );
        },
      },
      {
        title: "Action",
        width: 150,
        render: (text: any, member: GroupMemberType) => this._renderDropDownActionButtons(member),
      },
    ];
  }

  async componentDidMount() {
    await this._fetchMembers();
  }

  render() {
    // rowSelection object indicates the need for row selection
    const rowSelection = {
      onChange: (selectedRowKeys: React.Key[], selectedRows: GroupMemberType[]) => {
        this.bulkSelected = selectedRows;
      },
      // getCheckboxProps: (record: GroupMemberType) => ({
      //   disabled: record.status === "Inactivated", // Column configuration not to be checked
      //   email: record.email,
      // }),
    };

    const pagination = this.keyword
      ? false
      : {
          pageSize: this.pageSize,
          total: this.totalMembers,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          onShowSizeChange: this._onPaginationSizeChange,
        };

    const handleChange = (value: string) => {
      this.setState(() => {
        this.value = value;
      });
    };

    const emails = this.bulkSelected.map((e) => (
      <MuiTypography key={e.email} style={{ fontWeight: "bold" }}>
        {e.email}
      </MuiTypography>
    ));

    const bulkEdit = () => {
      Modal.confirm({
        title: "Bulk edit",
        content: (
          <div>
            <MuiTypography variant="body2" style={{ marginBottom: 15 }}>
              You have selected the following members to edit:
            </MuiTypography>
            {emails}
            <div style={{ marginTop: 15, marginBottom: 15 }}>
              Please select an option from the list.
            </div>
            <Select
              defaultValue="send_invite_email"
              id="simple-select-outlined"
              onChange={handleChange}
              style={{ width: 200 }}
            >
              <Option value={"send_invite_email"}>Send invite email</Option>
              <Option value={"reactivate"}>Reactivate</Option>
              <Option value={"deactivate"}>Deactivate</Option>
              <Option value={"delete"}>Delete</Option>
            </Select>
          </div>
        ),
        okText: "Confirm",
        width: CONST.MODAL_CONFIRM_WIDTH,
        onOk: async () => {
          const payload = {
            emails: this.bulkSelected.map((e) => e.email),
            action: this.value,
          };
          postBulkEdit(payload);
          this._fetchMembers();
        },
      });
    };

    const moveGroup = () => {
      Modal.confirm({
        title: "Change group",
        content: (
          <div>
            <MuiTypography variant="body2" style={{ marginBottom: 15 }}>
              You have selected the following members to change group:
            </MuiTypography>
            {emails}
            <div style={{ marginTop: 15, marginBottom: 15 }}>
              Please select a group from the list.
            </div>
            <Select
              defaultValue=""
              id="simple-select-outlined"
              onChange={handleChange}
              style={{ width: 200 }}
            >
              {this.groups.map((group) => (
                <Option value={group.group_id}>{group.name}</Option>
              ))}
            </Select>
          </div>
        ),
        okText: "Confirm",
        width: CONST.MODAL_CONFIRM_WIDTH,
        onOk: async () => {
          const payload = {
            emails: this.bulkSelected.map((e) => e.email),
            new_group_id: this.value,
          };
          await postMoveGroups(payload);
          this._fetchMembers();
        },
      });
    };

    // @ts-ignore
    return (
      <div>
        <article>All members</article>
        <div className={styles.container}>
          <div className={styles.row_box}>
            <Grid container spacing={3} justify="space-between">
              <Grid item xs={6}>
                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  style={{ marginRight: "10px" }}
                  disabled={this.bulkSelected.length < 1}
                  onClick={bulkEdit}
                >
                  Bulk edit
                </Button>
                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  style={{ marginRight: "10px" }}
                  disabled={this.bulkSelected.length < 1}
                  onClick={moveGroup}
                >
                  Change group
                </Button>
                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  onClick={this._inviteNewMember}
                >
                  Add Members
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Input.Search
                  className={styles.email_search}
                  placeholder="Search email..."
                  enterButton="SEARCH"
                  onSearch={this._onSearchEmail}
                />
              </Grid>
            </Grid>
          </div>
          <Table
            className={styles.table}
            rowSelection={{
              type: "checkbox",
              ...rowSelection,
            }}
            dataSource={toJS(this.members)}
            rowKey={(member: GroupMemberType) => member.email}
            columns={this.columns}
            loading={this.loading}
            pagination={pagination}
            // @ts-ignore
            onChange={this._onTableChange}
          />
        </div>
      </div>
    );
  }

  _renderDropDownActionButtons = (member: GroupMemberType) => {
    const {
      group_id,
      membership: { valid_days },
    } = member;

    let items: Array<{ label: string; disabled: boolean }> = [];
    if (group_id === CONST.B2C_GROUP_ID) {
      items = [
        { label: "Send invite email", disabled: true },
        { label: "Deactivate", disabled: true },
        { label: "Delete", disabled: true },
        { label: "Reactivate", disabled: true },
      ];
    } else if (member.status === "Inactivated") {
      items = [
        { label: "Send invite email", disabled: false },
        { label: "Deactivate", disabled: false },
        { label: "Delete", disabled: false },
        { label: "Reactivate", disabled: true },
      ];
    } else if (valid_days > 0) {
      // Trial
      items = [
        { label: "Send invite email", disabled: true },
        { label: "Deactivate", disabled: false },
        { label: "Delete", disabled: false },
        { label: "Reactivate", disabled: true },
      ];
    } else if (valid_days === -1) {
      // Active
      items = [
        { label: "Send invite email", disabled: true },
        { label: "Deactivate", disabled: false },
        { label: "Delete", disabled: false },
        { label: "Reactivate", disabled: true },
      ];
    } else if (valid_days === 0) {
      // Deactivated
      items = [
        { label: "Send invite email", disabled: true },
        { label: "Deactivate", disabled: true },
        { label: "Delete", disabled: false },
        { label: "Reactivate", disabled: false },
      ];
    }

    const buttons = (
      <Menu onClick={(param) => this._onClickActionButton(param.key, member)}>
        {items.map((item) => (
          <Menu.Item key={item.label} disabled={item.disabled}>
            {item.label}
          </Menu.Item>
        ))}
      </Menu>
    );

    const validButton = items.find((item) => !item.disabled);
    const buttonLabel = validButton ? validButton.label : "B2C User";
    return (
      <Dropdown overlay={buttons}>
        <Button>
          {buttonLabel} <Icon type="down" />
        </Button>
      </Dropdown>
    );
  };

  _onTableChange = async (
    pagination: { current?: number }, // when search with email keyword, there is no pagination on the page
    // filters: { group?: Array<number>; status?: Array<'Deactivated' | 'Not activated' | 'Active'> }
  ) => {
    const pageIndex = pagination.current ? pagination.current - 1 : 0;
    // this.filteredGroupIds = filters.group || [];
    // this.filteredStatus = filters.status || [];

    await this._fetchMembers(pageIndex);
  };

  _onPaginationSizeChange = (current: number, size: number) => {
    this.pageSize = size;
  };

  _fetchMembers = async (pageIndex: number = 0) => {
    try {
      this.loading = true;

      if (this.groups.length === 0) {
        this.groups = (await QUERY<{ groups: Array<GroupType> }>(GET_GROUPS)).groups;
      }

      const groupIds =
        this.filteredGroupIds.length === 0
          ? this.groups.map((e) => e.group_id)
          : this.filteredGroupIds;
      const offset = this.keyword ? 0 : this.pageSize * pageIndex;
      const size = this.keyword ? 1000 : this.pageSize;
      this.allMemberData = await getAllMembers(this.groups.map((i) => i.group_id));
      const res = await QUERY<{
        groupMembers: Array<GroupMemberType>;
        totalMembers: number;
        statistics: { emails: Array<string> };
      }>(GET_MEMBERS, {
        groupIds,
        offset,
        size,
        keyword: this.keyword,
        status: this.filteredStatus,
      });
      res.groupMembers.forEach((e, i) => {
        e.key = i + 1 + offset;
        e.created_at = dayjs(e.created_at).format("DD.MM.YYYY");

        const group = this.groups.find((group) => group.group_id === e.group_id);
        if (group) {
          e.group_name = group.name;
        }

        e.active_in_30_days = res.statistics.emails.includes(e.email);
      });

      runInAction(() => {
        this.members = res.groupMembers;
        this.totalMembers = res.totalMembers;
      });
    } catch (e) {
      toast.error("Something went wrong...", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } finally {
      this.loading = false;
    }
  };

  _onSearchEmail = async (email: string) => {
    if (this.groups.length > 0) {
      try {
        this.loading = true;

        const groupIds =
          this.filteredGroupIds.length === 0
            ? this.groups.map((e) => e.group_id)
            : this.filteredGroupIds;
        const res = await QUERY<{
          groupMembers: Array<GroupMemberType>;
          totalMembers: number;
        }>(GET_MEMBERS, {
          groupIds,
          offset: 0,
          size: 1000,
          keyword: email,
          status: this.filteredStatus,
        });
        res.groupMembers.forEach((e, i) => {
          e.key = i + 1;
          e.created_at = dayjs(e.created_at).format("DD.MM.YYYY");

          const group = this.groups.find((group) => group.group_id === e.group_id);
          if (group) {
            e.group_name = group.name;
          }
        });

        this.members = res.groupMembers;
      } catch (e) {
        toast.error("Something went wrong...", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
      } finally {
        runInAction(() => {
          this.loading = false;
          this.keyword = email;
        });
      }
    }
  };

  _inviteNewMember = () => {
    this.props.history.push("/admin/invite-member");
  };

  _onClickActionButton = (
    key: "Send invite email" | "Deactivate" | "Delete" | "Reactivate" | string,
    member: GroupMemberType,
  ) => {
    switch (key) {
      case "Send invite email":
        this._reInviteMember(member);
        break;
      case "Deactivate":
        this._deactivateMember(member);
        break;
      case "Delete":
        this._deleteMember(member);
        break;
      case "Reactivate":
        this._reactivate(member);
        break;
    }
  };

  _reInviteMember = (member: GroupMemberType) => {
    Modal.confirm({
      title: "Do you want to send another email invitation?",
      content: (
        <div>
          Yomento sends 3 automatic re-invitational emails if the member has not yet activated their
          account. We do this:
          <div style={{ marginTop: 10 }}>
            • One day after first invitation
            <br />• Two day after first invitation
            <br />• 7 day after first invitation
          </div>
        </div>
      ),
      okText: "Send invite email",
      width: CONST.MODAL_CONFIRM_WIDTH,
      onOk: async () => {
        try {
          await QUERY(RE_INVITE_MEMBER, { email: member.email });
        } catch (e) {
          Modal.error({
            title: e.message,
          });
        }
      },
    });
  };

  _upgradeToProMember = (member: GroupMemberType) => {
    Modal.confirm({
      title: "Please confirm upgrade!",
      content: `By upgrading this member to Pro Membership, you accept that your organization will be charged according to the agreed price plan.`,
      okText: "Upgrade",
      width: CONST.MODAL_CONFIRM_WIDTH,
      onOk: async () => {
        try {
          const newMembership = Membership.types.find((e) => e.valid_days < 0);
          if (newMembership) {
            const foundMember = this.members.find((e) => e.email === member.email);
            if (foundMember) {
              const params = {
                membership_id: newMembership.id,
                email: member.email,
              };
              foundMember.membership = (
                await QUERY<{ member: GroupMemberType }>(UPDATE_MEMBER_SHIP, params)
              ).member.membership;
            }
          }
        } catch (e) {
          toast.error(e.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      },
    });
  };

  _deactivateMember = (member: GroupMemberType) => {
    Modal.confirm({
      title: "Deactivate member",
      content: (
        <div>
          By ending this member’s Pro Membership, they will not be able to access any content in the
          Yomento app anymore.
          <div style={{ marginTop: 10 }}>We will notify the member through email about this.</div>
        </div>
      ),
      okText: "Deactivate",
      okButtonProps: { type: "danger" },
      width: CONST.MODAL_CONFIRM_WIDTH,
      onOk: async () => {
        try {
          const newMembership = Membership.types.find((e) => e.valid_days === 0);
          if (newMembership) {
            const foundMember = this.members.find((e) => e.email === member.email);
            if (foundMember) {
              const params = {
                membership_id: newMembership.id,
                email: member.email,
              };

              await QUERY<{ member: GroupMemberType }>(UPDATE_MEMBER_SHIP, params);
            }
          }
        } catch (e) {
          toast.error(e.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      },
    });
  };

  _deleteMember = (member: GroupMemberType) => {
    Modal.confirm({
      title: "Delete member",
      content: (
        <div>
          By deleting this member, they will be removed from the group. If you only want to pause
          the account, use the deactivate option.
          <div style={{ marginTop: 10 }}>We will notify the member through email about this.</div>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { type: "danger" },
      width: CONST.MODAL_CONFIRM_WIDTH,
      onOk: async () => {
        try {
          await MUTATE<string>(DELETE_MEMBER, {
            email: member.email,
          });
          this.members = this.members.filter((e) => e.email !== member.email);
        } catch (e) {
          toast.error(e.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      },
    });
  };

  _reactivate = (member: GroupMemberType) => {
    Modal.confirm({
      title: "Reactivate member",
      content:
        "By reactivating this member, you accept that your organization will be charged according to the agreed price plan.",
      okText: "Reactivate",
      width: CONST.MODAL_CONFIRM_WIDTH,
      onOk: async () => {
        try {
          const newMembership = Membership.types.find((e) => e.valid_days < 0);
          if (newMembership) {
            const foundMember = this.members.find((e) => e.email === member.email);
            if (foundMember) {
              const params = {
                membership_id: newMembership.id,
                email: member.email,
              };
              foundMember.membership = (
                await QUERY<{ member: GroupMemberType }>(UPDATE_MEMBER_SHIP, params)
              ).member.membership;
            }
          }
        } catch (e) {
          toast.error(e.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      },
    });
  };
}

const GET_GROUPS = `
query {
  groups {
    group_id
    name
    total_number
  }
}`;

interface GroupType {
  group_id: number;
  name: string;
  total_number: number;
}

const GET_MEMBERS = `
query(
  $groupIds: [Int!]!
  $size: Int!
  $offset: Int!
  $keyword: String
  $status: [GroupMemberStatus!]
) {
  totalMembers: countGroupMembers(
    groupIds: $groupIds
    keyword: $keyword
    status: $status
  )
  groupMembers(
    groupIds: $groupIds
    size: $size
    offset: $offset
    keyword: $keyword
    status: $status
  ) {
    name
    email
    group_id
    created_at
    activate_at
    status
    membership {
      valid_days
      type
    }
    access_code
  }
  statistics {
    emails: active_30_days_user_emails
  }
}`;
export const UPDATE_MEMBER_SHIP = `
mutation($membership_id: Int!, $email: EmailAddress!) {
  member: updateMembership(membership_id: $membership_id, email: $email) {
    name
    email
    group_id
    created_at
    activate_at
    status
    membership {
      valid_days
      type
    }
    access_code
  }
}`;
export const RE_INVITE_MEMBER = `
mutation($email: EmailAddress!) {
  reInviteMember(email: $email)
}`;

export const DELETE_MEMBER = `
mutation($email: EmailAddress!) {
  deleteMemberFromDashboard(email: $email)
}`;

export interface GroupMemberType {
  key: number;
  name: string;
  email: string;
  group_id: number;
  group_name?: string;
  created_at: string;
  activate_at: string;
  status: "Inactivated" | "Activated";
  access_code: string;
  membership: {
    valid_days: number;
    type: string;
  };
  active_in_30_days: boolean;
}
